/**
 * BatchSelectionModal Component
 * Allows users to optionally select batches to pay alongside their power report submission
 * Supports partial payments - users can modify NET, BUF, MISC amounts per batch
 * Tailwind version using OathUI components
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ShoppingCartIcon,
  ForwardIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon
} from '@heroicons/react/24/outline';
import { OathModal, OathButton, OathSpinner, OathAlert } from '../../../shared/OathUI';
import { getBatchesForSubmission } from '../../../../services/oathPowerReportService';
import { formatCurrencyWithSymbol } from '../utils';
import { clampCurrency, roundCurrency, getBatchId, getBatchTypeBadge, isLinkableBatch, formatAmountOnBlur, nonNegativeAmount, BATCH_TYPE_LABELS, BATCH_TYPE_BADGES } from '../utils/formatters';
import { useClickVsDrag } from '../utils/hooks';

// Chip color variants — keyed by the `color` field on each BATCH_TYPE_BADGES entry.
// Tailwind JIT can't generate classes from template literals, so the variants are written out.
const CHIP_VARIANTS = {
  blue: {
    inactive: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 hover:border-blue-300 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800 dark:hover:bg-blue-900/40',
    active:   'bg-blue-200 text-blue-800 border-blue-400 dark:bg-blue-900/60 dark:text-blue-200 dark:border-blue-400',
    countInactive: 'bg-blue-100 text-blue-700 dark:bg-blue-800 dark:text-blue-200',
    countActive:   'bg-blue-700 text-white dark:bg-blue-300 dark:text-blue-900',
  },
  purple: {
    inactive: 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100 hover:border-purple-300 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800 dark:hover:bg-purple-900/40',
    active:   'bg-purple-200 text-purple-800 border-purple-400 dark:bg-purple-900/60 dark:text-purple-200 dark:border-purple-400',
    countInactive: 'bg-purple-100 text-purple-700 dark:bg-purple-800 dark:text-purple-200',
    countActive:   'bg-purple-700 text-white dark:bg-purple-300 dark:text-purple-900',
  },
  amber: {
    inactive: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 hover:border-amber-300 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800 dark:hover:bg-amber-900/40',
    active:   'bg-amber-200 text-amber-800 border-amber-400 dark:bg-amber-900/60 dark:text-amber-200 dark:border-amber-400',
    countInactive: 'bg-amber-100 text-amber-700 dark:bg-amber-800 dark:text-amber-200',
    countActive:   'bg-amber-700 text-white dark:bg-amber-300 dark:text-amber-900',
  },
  rose: {
    inactive: 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100 hover:border-rose-300 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-800 dark:hover:bg-rose-900/40',
    active:   'bg-rose-200 text-rose-800 border-rose-400 dark:bg-rose-900/60 dark:text-rose-200 dark:border-rose-400',
    countInactive: 'bg-rose-100 text-rose-700 dark:bg-rose-800 dark:text-rose-200',
    countActive:   'bg-rose-700 text-white dark:bg-rose-300 dark:text-rose-900',
  },
  emerald: {
    inactive: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800 dark:hover:bg-emerald-900/40',
    active:   'bg-emerald-200 text-emerald-800 border-emerald-400 dark:bg-emerald-900/60 dark:text-emerald-200 dark:border-emerald-400',
    countInactive: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-800 dark:text-emerald-200',
    countActive:   'bg-emerald-700 text-white dark:bg-emerald-300 dark:text-emerald-900',
  },
};

/**
 * Clickable column header. Cycles asc → desc → unsorted on each click.
 * Always shows a chevron indicator so the header reads as sortable —
 * a faint up/down chevron when inactive, the directional chevron tinted
 * primary when this column is the active sort.
 */
const SortableTh = ({ sortKey, sortConfig, onSort, align = 'left', children }) => {
  const isActive = sortConfig.key === sortKey;
  const Chevron = isActive
    ? (sortConfig.direction === 'asc' ? ChevronUpIcon : ChevronDownIcon)
    : ChevronUpDownIcon;
  const alignClass = align === 'right' ? 'text-right' : 'text-left';
  const justifyClass = align === 'right' ? 'justify-end' : 'justify-start';
  return (
    <th
      onClick={() => onSort(sortKey)}
      title="Click to sort"
      aria-sort={isActive ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      // Each th gets its own opaque background — sticky thead doesn't repaint cells, so
      // without this, scrolling rows bleed through the active-sort tint.
      className={`px-2 py-2 text-xs font-semibold uppercase whitespace-nowrap cursor-pointer select-none transition-colors ${alignClass} ${
        isActive
          ? 'text-primary dark:text-primary-light bg-primary-50 dark:bg-primary-900'
          : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
      }`}
    >
      <span className={`inline-flex items-center gap-1 ${justifyClass}`}>
        {children}
        <Chevron className={`h-3.5 w-3.5 ${isActive ? '' : 'opacity-50'}`} />
      </span>
    </th>
  );
};

const BatchSelectionModal = ({
  isOpen,
  onClose,
  onContinue,  // Called with (selectedBatches, paymentAmounts, batchesData)
  onSkip,      // Called when user wants to skip batch selection
  agencyId,
  report,      // Current power report being submitted
  // Optional restore-state used when SubmitReportModal navigates Back here.
  initialSelectedBatches = null,   // Set<batchId> | null
  initialPaymentAmounts = null     // { batchId: { net, buf, misc } } | null  (nested form, same shape sent to onContinue)
}) => {
  const [batches, setBatches] = useState([]);
  const [selectedBatches, setSelectedBatches] = useState(new Set());
  // Flat state structure like BatchesToPayTab: { "batchId-net": value, "batchId-buf": value, ... }
  const [paymentAmounts, setPaymentAmounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Use click vs drag hook to prevent row toggle when user drags to select text in inputs
  const { handleMouseDown, isClick } = useClickVsDrag();

  // Load batches when modal opens
  useEffect(() => {
    const loadBatches = async () => {
      if (!isOpen || !agencyId) return;

      setLoading(true);
      setError(null);

      try {
        const result = await getBatchesForSubmission(agencyId);
        if (result.success) {
          const loadedBatches = result.data || [];
          // Defense-in-depth: drop batches with nothing payable. A batch survives if at
          // least one bucket is positive — mixed-sign batches (e.g. +NET, -BUF) are still
          // selectable, but their credit buckets will display $0 in the editable inputs.
          const validBatches = loadedBatches.filter(batch => {
            return nonNegativeAmount(batch.totalNet) + nonNegativeAmount(batch.totalBuf) + nonNegativeAmount(batch.totalMisc) > 0;
          });
          setBatches(validBatches);

          // Hydrate selection from restore-state if provided (Back from SubmitReportModal),
          // else start with nothing selected.
          setSelectedBatches(initialSelectedBatches ? new Set(initialSelectedBatches) : new Set());

          // Initialize amounts with full due amounts (flat structure) for when user selects.
          // Stored as 2-decimal strings so the inputs render "17.50" instead of "17.5".
          // If restore-state is provided, override the defaults with the user's prior edits.
          if (validBatches.length > 0) {
            const initialAmounts = {};
            validBatches.forEach(batch => {
              const batchId = getBatchId(batch);
              // Credits seed at 0 — the visible /due labels still show the full credit amount.
              initialAmounts[`${batchId}-net`] = formatAmountOnBlur(nonNegativeAmount(batch.totalNet));
              initialAmounts[`${batchId}-buf`] = formatAmountOnBlur(nonNegativeAmount(batch.totalBuf));
              initialAmounts[`${batchId}-misc`] = formatAmountOnBlur(nonNegativeAmount(batch.totalMisc));
            });
            if (initialPaymentAmounts) {
              Object.entries(initialPaymentAmounts).forEach(([batchId, vals]) => {
                if (vals?.net !== undefined) initialAmounts[`${batchId}-net`] = formatAmountOnBlur(nonNegativeAmount(vals.net));
                if (vals?.buf !== undefined) initialAmounts[`${batchId}-buf`] = formatAmountOnBlur(nonNegativeAmount(vals.buf));
                if (vals?.misc !== undefined) initialAmounts[`${batchId}-misc`] = formatAmountOnBlur(nonNegativeAmount(vals.misc));
              });
            }
            setPaymentAmounts(initialAmounts);
          }
        } else {
          setError('Failed to load outstanding batches. You can still submit your report without batch payments.');
        }
      } catch (err) {
        console.error('Failed to load batches to pay:', err);
        setError('Failed to load outstanding batches. You can still submit your report without batch payments.');
      } finally {
        setLoading(false);
      }
    };

    loadBatches();
    // initialSelectedBatches/initialPaymentAmounts are intentionally read at open-time only;
    // re-firing on each prop change would clobber in-modal edits while the user is selecting.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, agencyId]);

  // Reset selection when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedBatches(new Set());
      setPaymentAmounts({});
      setSearchQuery('');
      setTypeFilter(new Set());
      setSortConfig({ key: null, direction: 'asc' });
    }
  }, [isOpen]);

  // Handle batch toggle (used by checkbox)
  const handleBatchToggle = useCallback((batchId, batch) => {
    setSelectedBatches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(batchId)) {
        newSet.delete(batchId);
      } else {
        newSet.add(batchId);
        // Initialize amounts for newly selected batch (formatted to 2 decimals for display).
        // Credit buckets seed at 0 — they're visible on the row but excluded from payment.
        setPaymentAmounts(prevAmounts => ({
          ...prevAmounts,
          [`${batchId}-net`]: formatAmountOnBlur(nonNegativeAmount(batch.totalNet)),
          [`${batchId}-buf`]: formatAmountOnBlur(nonNegativeAmount(batch.totalBuf)),
          [`${batchId}-misc`]: formatAmountOnBlur(nonNegativeAmount(batch.totalMisc))
        }));
      }
      return newSet;
    });
  }, []);

  // Handle row click - only toggle if it was a click, not a drag (for text selection in inputs)
  const handleRowClick = useCallback((e, batchId, batch, isSelected) => {
    if (isClick(e)) {
      handleBatchToggle(batchId, batch);
    }
  }, [isClick, handleBatchToggle]);

  // Simple amount change handler - just sets the value (same pattern as BatchesToPayTab)
  const handleAmountChange = useCallback((batchId, field, value) => {
    setPaymentAmounts(prev => ({
      ...prev,
      [`${batchId}-${field}`]: value
    }));
  }, []);

  // Counts per batch type — used by the filter chips and the empty-state hint.
  const typeCounts = useMemo(() => {
    const counts = {};
    batches.forEach(b => {
      if (b?.batchType) counts[b.batchType] = (counts[b.batchType] || 0) + 1;
    });
    return counts;
  }, [batches]);

  // Which known batch-type chips should appear (only types actually present in the data).
  const availableTypes = useMemo(() => {
    return Object.keys(BATCH_TYPE_LABELS).filter(t => (typeCounts[t] || 0) > 0);
  }, [typeCounts]);

  const toggleTypeFilter = useCallback((type) => {
    setTypeFilter(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  // Filter batches by type chips, then by search query.
  // Search matches batch #, defendant, associated report #, AND amounts (NET / BUF / MISC / total).
  // Amount matching is substring-based on the unformatted string, so "100" finds $100.00 and $1,000.50.
  const filteredBatches = useMemo(() => {
    let result = batches;

    if (typeFilter.size > 0) {
      result = result.filter(b => typeFilter.has(b?.batchType));
    }

    const q = searchQuery.trim().toLowerCase().replace(/[$,]/g, '');
    if (q) {
      result = result.filter(batch => {
        const batchId = String(getBatchId(batch) ?? '').toLowerCase();
        const reportNumber = String(batch.reportNumber ?? '').toLowerCase();
        const defendant = String(batch.defendantName ?? '').toLowerCase();
        const associated = String(batch.associatedReportNumber ?? '').toLowerCase();
        const net  = String(batch.totalNet  ?? '');
        const buf  = String(batch.totalBuf  ?? '');
        const misc = String(batch.totalMisc ?? '');
        const total = String((batch.totalNet || 0) + (batch.totalBuf || 0) + (batch.totalMisc || 0));
        return (
          batchId.includes(q) ||
          reportNumber.includes(q) ||
          defendant.includes(q) ||
          associated.includes(q) ||
          net.includes(q) ||
          buf.includes(q) ||
          misc.includes(q) ||
          total.includes(q)
        );
      });
    }

    return result;
  }, [batches, typeFilter, searchQuery]);

  // Click a column header to cycle through asc → desc → unsorted.
  const handleSort = useCallback((key) => {
    setSortConfig(prev => {
      if (prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return { key: null, direction: 'asc' };
    });
  }, []);

  // Sorted view of filteredBatches. Sorts by the original due amounts (totalNet etc.) —
  // user-edited payment amounts don't move the row, which would feel jumpy.
  const sortedBatches = useMemo(() => {
    if (!sortConfig.key) return filteredBatches;
    const getValue = (batch) => {
      switch (sortConfig.key) {
        case 'reportNumber': return String(batch.reportNumber || getBatchId(batch) || '').toLowerCase();
        case 'description':
          return String(
            batch.batchType === 'TransferBond'
              ? (batch.defendantName || '')
              : (batch.associatedReportNumber || '')
          ).toLowerCase();
        case 'net':   return batch.totalNet  || 0;
        case 'buf':   return batch.totalBuf  || 0;
        case 'misc':  return batch.totalMisc || 0;
        case 'total': return (batch.totalNet || 0) + (batch.totalBuf || 0) + (batch.totalMisc || 0);
        default: return 0;
      }
    };
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return [...filteredBatches].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
      if (typeof av === 'string') return av.localeCompare(bv) * dir;
      return (av - bv) * dir;
    });
  }, [filteredBatches, sortConfig]);

  // Handle select all — operates on the currently-visible (filtered) batches.
  // Checking selects all visible; unchecking clears the entire selection (matches BatchesToPayTab).
  const handleSelectAll = useCallback((checked) => {
    if (checked) {
      const visibleIds = new Set(filteredBatches.map(b => getBatchId(b)));
      setSelectedBatches(visibleIds);
      setPaymentAmounts(prev => {
        const next = { ...prev };
        filteredBatches.forEach(batch => {
          const batchId = getBatchId(batch);
          // Credits seed at 0; positive buckets seed at the full due amount.
          next[`${batchId}-net`] = formatAmountOnBlur(nonNegativeAmount(batch.totalNet));
          next[`${batchId}-buf`] = formatAmountOnBlur(nonNegativeAmount(batch.totalBuf));
          next[`${batchId}-misc`] = formatAmountOnBlur(nonNegativeAmount(batch.totalMisc));
        });
        return next;
      });
    } else {
      setSelectedBatches(new Set());
    }
  }, [filteredBatches]);

  // Calculate totals with proper currency rounding (same pattern as BatchesToPayTab)
  const totals = useMemo(() => {
    let totalNet = 0;
    let totalBuf = 0;
    let totalMisc = 0;

    selectedBatches.forEach(batchId => {
      const netVal = paymentAmounts[`${batchId}-net`];
      const bufVal = paymentAmounts[`${batchId}-buf`];
      const miscVal = paymentAmounts[`${batchId}-misc`];
      totalNet += roundCurrency(nonNegativeAmount(netVal));
      totalBuf += roundCurrency(nonNegativeAmount(bufVal));
      totalMisc += roundCurrency(nonNegativeAmount(miscVal));
    });

    return {
      totalNet: roundCurrency(totalNet),
      totalBuf: roundCurrency(totalBuf),
      totalMisc: roundCurrency(totalMisc),
      grandTotal: roundCurrency(totalNet + totalBuf + totalMisc)
    };
  }, [selectedBatches, paymentAmounts]);

  const allSelected = filteredBatches.length > 0 && filteredBatches.every(b => selectedBatches.has(getBatchId(b)));

  // Handle continue - convert flat state to nested format expected by SubmitReportModal
  const handleContinue = useCallback(() => {
    const selectedBatchData = batches.filter(b => selectedBatches.has(getBatchId(b)));

    // Convert flat payment amounts to nested format for SubmitReportModal.
    // Values may be strings (e.g. "17.50") because we display formatted strings in the
    // inputs — coerce to non-negative numbers here so downstream math doesn't string-concat
    // and credits never leak into the payload.
    const nestedAmounts = {};
    selectedBatches.forEach(batchId => {
      nestedAmounts[batchId] = {
        net: nonNegativeAmount(paymentAmounts[`${batchId}-net`]),
        buf: nonNegativeAmount(paymentAmounts[`${batchId}-buf`]),
        misc: nonNegativeAmount(paymentAmounts[`${batchId}-misc`])
      };
    });

    onContinue(selectedBatches, nestedAmounts, selectedBatchData);
  }, [batches, selectedBatches, paymentAmounts, onContinue]);

  const reportTotal = (report?.totalNet || 0) + (report?.totalBuf || 0);
  const grandTotal = reportTotal + totals.grandTotal;

  // Hero panel (Payment Summary) — rendered twice with different sizing wrappers:
  // above the filters on mobile, alongside the data on desktop. Same JSX, single source of truth.
  const heroPanel = (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-700 via-primary to-primary-light text-white p-4 sm:p-5 shadow-lg">
      <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-white/10 pointer-events-none" />
      <div className="absolute -bottom-16 -left-10 h-32 w-32 rounded-full bg-white/5 pointer-events-none" />

      <p className="relative text-[11px] uppercase tracking-[0.18em] text-white/70 mb-3">
        {selectedBatches.size > 0 ? 'Combined Payment Summary' : 'Payment Summary'}
      </p>

      {selectedBatches.size > 0 ? (
        <div className="relative space-y-1.5 text-sm">
          <div className="flex justify-between gap-2">
            <span className="text-white/85">Power Report (NET + BUF)</span>
            <span className="font-semibold tabular-nums">{formatCurrencyWithSymbol(reportTotal)}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-white/85">
              Batch Payments&nbsp;
              <span className="text-xs text-white/70 font-semibold">×{selectedBatches.size}</span>
            </span>
            <span className="font-semibold tabular-nums">{formatCurrencyWithSymbol(totals.grandTotal)}</span>
          </div>
          {totals.totalNet > 0 && (
            <div className="flex justify-between gap-2 pl-3 text-xs text-white/75">
              <span>NET</span>
              <span className="tabular-nums">{formatCurrencyWithSymbol(totals.totalNet)}</span>
            </div>
          )}
          {totals.totalBuf > 0 && (
            <div className="flex justify-between gap-2 pl-3 text-xs text-white/75">
              <span>BUF</span>
              <span className="tabular-nums">{formatCurrencyWithSymbol(totals.totalBuf)}</span>
            </div>
          )}
          {totals.totalMisc > 0 && (
            <div className="flex justify-between gap-2 pl-3 text-xs text-white/75">
              <span>MISC</span>
              <span className="tabular-nums">{formatCurrencyWithSymbol(totals.totalMisc)}</span>
            </div>
          )}
        </div>
      ) : (
        <div className="relative space-y-2 text-sm">
          <div className="flex justify-between gap-2">
            <span className="text-white/85">Power Report (NET + BUF)</span>
            <span className="font-semibold tabular-nums">{formatCurrencyWithSymbol(reportTotal)}</span>
          </div>
          <p className="text-xs text-white/70 italic">
            Submitting report only. Select a batch below to roll it into one combined payment.
          </p>
        </div>
      )}
    </div>
  );

  return (
    <OathModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <ShoppingCartIcon className="h-6 w-6 text-primary dark:text-primary-light" />
          <span>Pay Outstanding Batches?</span>
        </div>
      }
      size="2xl"
      footer={
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto sm:ml-auto">
          <OathButton
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="w-full sm:w-auto"
          >
            Cancel
          </OathButton>
          <OathButton
            variant="secondary"
            onClick={onSkip}
            disabled={loading}
            icon={ForwardIcon}
            className="w-full sm:w-auto"
          >
            Skip - Submit Report Only
          </OathButton>
          <OathButton
            onClick={handleContinue}
            disabled={loading}
            className="w-full sm:w-auto"
          >
            {selectedBatches.size > 0 ? (
              <>
                Continue with {selectedBatches.size} Batch{selectedBatches.size !== 1 ? 'es' : ''}
                <span className="mx-1.5 opacity-60">·</span>
                <span className="tabular-nums">{formatCurrencyWithSymbol(grandTotal)}</span>
              </>
            ) : (
              'Continue'
            )}
          </OathButton>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Report Details — same look as the SubmitReportModal Report Details card */}
        <div className="rounded-2xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
          <p className="text-[11px] uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400 mb-3">
            Report Details
          </p>

          {/* Agency + Report — stacked on mobile, side-by-side on desktop */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 mb-3 sm:mb-4">
            <div className="min-w-0">
              <p className="text-xs text-gray-500 dark:text-gray-400">Agency</p>
              <p
                className="text-base font-bold text-gray-900 dark:text-white break-words sm:truncate"
                title={`${report?.agencyName || ''}${report?.agencyId ? ` (${report.agencyId})` : ''}`}
              >
                {report?.agencyName || '—'}
                {report?.agencyId && (
                  <span className="ml-1 text-sm font-medium text-gray-500 dark:text-gray-400">({report.agencyId})</span>
                )}
              </p>
            </div>
            {report?.reportNumber && (
              <div className="min-w-0">
                <p className="text-xs text-gray-500 dark:text-gray-400">Report</p>
                <p className="font-semibold text-gray-900 dark:text-white break-all sm:break-normal sm:truncate" title={report.reportNumber}>
                  {report.reportNumber}
                </p>
              </div>
            )}
          </div>

          {/* Mobile: each metric is a label/value row */}
          <div className="sm:hidden divide-y divide-gray-200 dark:divide-gray-700 text-sm">
            <div className="flex justify-between gap-3 py-1.5">
              <span className="text-gray-500 dark:text-gray-400">Powers</span>
              <span className="font-semibold text-gray-900 dark:text-white tabular-nums">{report?.powerCount || 0}</span>
            </div>
            <div className="flex justify-between gap-3 py-1.5">
              <span className="text-gray-500 dark:text-gray-400">NET</span>
              <span className="font-semibold text-gray-900 dark:text-white tabular-nums">{formatCurrencyWithSymbol(report?.totalNet)}</span>
            </div>
            <div className="flex justify-between gap-3 py-1.5">
              <span className="text-gray-500 dark:text-gray-400">BUF</span>
              <span className="font-semibold text-gray-900 dark:text-white tabular-nums">{formatCurrencyWithSymbol(report?.totalBuf)}</span>
            </div>
          </div>

          {/* Desktop: 3-col grid for Powers · NET · BUF */}
          <dl className="hidden sm:grid grid-cols-3 gap-x-4 gap-y-3 text-sm">
            <div>
              <dt className="text-xs text-gray-500 dark:text-gray-400">Powers</dt>
              <dd className="font-semibold text-gray-900 dark:text-white">{report?.powerCount || 0}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500 dark:text-gray-400">NET</dt>
              <dd className="font-semibold text-gray-900 dark:text-white">{formatCurrencyWithSymbol(report?.totalNet)}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500 dark:text-gray-400">BUF</dt>
              <dd className="font-semibold text-gray-900 dark:text-white">{formatCurrencyWithSymbol(report?.totalBuf)}</dd>
            </div>
          </dl>
        </div>

        {/* Batch Selection */}
        <div>
          {loading ? (
            <div className="flex justify-center py-8">
              <OathSpinner size="lg" />
            </div>
          ) : error ? (
            <OathAlert variant="warning" className="mb-4">
              {error}
            </OathAlert>
          ) : batches.length === 0 ? (
            <OathAlert variant="info" className="mb-4">
              No outstanding batches found. Click "Continue" to submit your power report.
            </OathAlert>
          ) : (
            <>
              {/* Quick search + filter chips on the same row at sm+; stacked on mobile */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-3">
                {/* Search input */}
                <div className="relative w-full sm:w-80 sm:flex-shrink-0">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search…"
                    className="w-full pl-9 pr-9 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm hover:border-gray-400 dark:hover:border-gray-500 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      aria-label="Clear search"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {/* Type filter chips — colored to match the inline badges in the data set */}
                {availableTypes.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 sm:flex-1 sm:min-w-0">
                    {availableTypes.map(type => {
                      const isActive = typeFilter.has(type);
                      const count = typeCounts[type] || 0;
                      const variant = CHIP_VARIANTS[BATCH_TYPE_BADGES[type]?.color] || CHIP_VARIANTS.blue;
                      return (
                        <button
                          key={type}
                          type="button"
                          onClick={() => toggleTypeFilter(type)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                            isActive ? variant.active : variant.inactive
                          }`}
                        >
                          <span>{BATCH_TYPE_LABELS[type]}</span>
                          <span
                            className={`px-1.5 py-0 rounded-full text-[10px] font-semibold tabular-nums ${
                              isActive ? variant.countActive : variant.countInactive
                            }`}
                          >
                            {count}
                          </span>
                        </button>
                      );
                    })}
                    {typeFilter.size > 0 && (
                      <button
                        type="button"
                        onClick={() => setTypeFilter(new Set())}
                        className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Table + desktop hero side-by-side on lg+. On mobile the hero lives above the
                  filters (rendered separately), so this row only contains the data on small screens. */}
              <div className="flex flex-col lg:flex-row gap-3 items-start">
              {/* Batches list — desktop table (lg+) and mobile cards (<lg) */}
              <div className="w-full lg:flex-1 min-w-0">
              {/* Mobile card list — flows naturally; the modal body is the single scroll surface
                  so the user doesn't fight nested scrollbars when there are hundreds of batches. */}
              <div className="lg:hidden border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                {/* Select-all header — equivalent of the desktop table's checkbox column */}
                {sortedBatches.length > 0 && (
                  <label className="flex items-center gap-3 px-3 py-2.5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="h-5 w-5 rounded border-gray-300 dark:border-gray-600 text-primary accent-primary focus:ring-primary dark:bg-gray-700"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Select all <span className="text-gray-500 dark:text-gray-400">({sortedBatches.length})</span>
                    </span>
                  </label>
                )}

                <div className="p-2 space-y-2">
                {sortedBatches.length === 0 ? (
                  <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-6">
                    No batches match your filters.
                  </p>
                ) : (
                  sortedBatches.map((batch) => {
                    const batchId = getBatchId(batch);
                    const isSelected = selectedBatches.has(batchId);
                    const netDue = batch.totalNet || 0;
                    const bufDue = batch.totalBuf || 0;
                    const miscDue = batch.totalMisc || 0;
                    const totalDue = netDue + bufDue + miscDue;
                    const typeBadge = getBatchTypeBadge(batch);
                    const netVal = paymentAmounts[`${batchId}-net`];
                    const bufVal = paymentAmounts[`${batchId}-buf`];
                    const miscVal = paymentAmounts[`${batchId}-misc`];
                    const payingTotal = isSelected
                      ? roundCurrency(
                          (netVal === '' || netVal === undefined ? 0 : Number(netVal)) +
                          (bufVal === '' || bufVal === undefined ? 0 : Number(bufVal)) +
                          (miscVal === '' || miscVal === undefined ? 0 : Number(miscVal))
                        )
                      : totalDue;

                    return (
                      <div
                        key={batchId}
                        onClick={() => handleBatchToggle(batchId, batch)}
                        className={`relative rounded-lg border-2 p-3 cursor-pointer transition-colors ${
                          isSelected
                            ? 'border-primary bg-primary-50/50 dark:bg-primary-900/20'
                            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleBatchToggle(batchId, batch)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-5 w-5 mt-0.5 rounded border-gray-300 dark:border-gray-600 text-primary accent-primary focus:ring-primary dark:bg-gray-700 flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {!isLinkableBatch(batch) ? (
                                <span className="font-semibold text-sm text-gray-900 dark:text-white">{batch.reportNumber || batchId}</span>
                              ) : (
                                <a
                                  href={`/app/power-inquiry?agencyIds=${batch.agencyId}&viewMode=byReport&batchId=${batchId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="font-semibold text-sm text-primary dark:text-primary-light hover:underline"
                                >
                                  {batch.reportNumber || batchId}
                                </a>
                              )}
                              {typeBadge && (
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${typeBadge.bgColor} ${typeBadge.textColor}`}>
                                  {typeBadge.label}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5" title={batch.batchType === 'TransferBond' ? batch.defendantName : batch.associatedReportNumber}>
                              {batch.batchType === 'TransferBond'
                                ? (batch.defendantName || '—')
                                : (batch.associatedReportNumber || '—')}
                            </p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Total</p>
                            <p className="text-sm font-bold text-primary dark:text-primary-light tabular-nums">
                              {formatCurrencyWithSymbol(payingTotal)}
                            </p>
                          </div>
                        </div>

                        {/* Editable amounts shown only when selected — vertically stacked for easy thumb editing */}
                        {isSelected && (
                          <div className="mt-3 pt-3 border-t border-primary/20 space-y-2" onClick={(e) => e.stopPropagation()}>
                            {[
                              { key: 'net',  label: 'NET',  due: netDue,  val: netVal },
                              { key: 'misc', label: 'MISC', due: miscDue, val: miscVal },
                              { key: 'buf',  label: 'BUF',  due: bufDue,  val: bufVal },
                            ].map(({ key, label, due, val }) => (
                              <div key={key} className="flex items-center gap-3">
                                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 w-10 flex-shrink-0">{label}</span>
                                <div className="flex-1 relative">
                                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400">$</span>
                                  <input
                                    type="number"
                                    value={val ?? formatAmountOnBlur(due)}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      if (v === '') handleAmountChange(batchId, key, '');
                                      else {
                                        const num = parseFloat(v);
                                        handleAmountChange(batchId, key, isNaN(num) ? '' : clampCurrency(num, due));
                                      }
                                    }}
                                    onBlur={(e) => handleAmountChange(batchId, key, formatAmountOnBlur(e.target.value))}
                                    disabled={due <= 0}
                                    min="0"
                                    max={due}
                                    step="0.01"
                                    className="w-full pl-6 pr-2 py-2 text-sm text-right tabular-nums border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 dark:text-white focus:ring-1 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  />
                                </div>
                                <span className="text-xs text-gray-500 dark:text-gray-400 w-20 text-right shrink-0 tabular-nums">
                                  / {formatCurrencyWithSymbol(due)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
                </div>
              </div>

              {/* Desktop table — capped at ~5 rows; scroll inside table only */}
              <div className="hidden lg:block border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden max-h-64 overflow-y-auto overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                    <tr>
                      <th className="px-2 py-2 w-8 bg-gray-50 dark:bg-gray-800">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary accent-primary focus:ring-primary dark:bg-gray-700"
                        />
                      </th>
                      <SortableTh sortKey="reportNumber" sortConfig={sortConfig} onSort={handleSort} align="left">Batch #</SortableTh>
                      <SortableTh sortKey="description"  sortConfig={sortConfig} onSort={handleSort} align="left">Description</SortableTh>
                      <SortableTh sortKey="net"   sortConfig={sortConfig} onSort={handleSort} align="right">NET</SortableTh>
                      <SortableTh sortKey="misc"  sortConfig={sortConfig} onSort={handleSort} align="right">MISC</SortableTh>
                      <SortableTh sortKey="buf"   sortConfig={sortConfig} onSort={handleSort} align="right">BUF</SortableTh>
                      <SortableTh sortKey="total" sortConfig={sortConfig} onSort={handleSort} align="right">Total</SortableTh>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                    {sortedBatches.map((batch) => {
                      const batchId = getBatchId(batch);
                      const isSelected = selectedBatches.has(batchId);
                      const netDue = batch.totalNet || 0;
                      const bufDue = batch.totalBuf || 0;
                      const miscDue = batch.totalMisc || 0;
                      const totalDue = netDue + bufDue + miscDue;
                      const typeBadge = getBatchTypeBadge(batch);

                      // Get current payment values
                      const netVal = paymentAmounts[`${batchId}-net`];
                      const bufVal = paymentAmounts[`${batchId}-buf`];
                      const miscVal = paymentAmounts[`${batchId}-misc`];

                      // Calculate paying total for this batch
                      const payingTotal = isSelected
                        ? roundCurrency(
                            (netVal === '' || netVal === undefined ? 0 : Number(netVal)) +
                            (bufVal === '' || bufVal === undefined ? 0 : Number(bufVal)) +
                            (miscVal === '' || miscVal === undefined ? 0 : Number(miscVal))
                          )
                        : totalDue;

                      return (
                        <tr
                          key={batchId}
                          onMouseDown={handleMouseDown}
                          onClick={(e) => handleRowClick(e, batchId, batch, isSelected)}
                          className={`cursor-pointer transition-colors ${
                            isSelected
                              ? 'bg-primary-50/50 dark:bg-primary-900/20'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleBatchToggle(batchId, batch)}
                              className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary accent-primary focus:ring-primary dark:bg-gray-700"
                            />
                          </td>
                          <td className="px-2 py-2 text-gray-900 dark:text-white whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              {!isLinkableBatch(batch) ? (
                                <span className="font-medium">{batch.reportNumber || batchId}</span>
                              ) : (
                                <a
                                  href={`/app/power-inquiry?agencyIds=${batch.agencyId}&viewMode=byReport&batchId=${batchId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="font-medium text-primary dark:text-primary-light hover:underline cursor-pointer"
                                >
                                  {batch.reportNumber || batchId}
                                </a>
                              )}
                              {typeBadge && (
                                <span className={`inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium ${typeBadge.bgColor} ${typeBadge.textColor}`}>
                                  {typeBadge.label}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-2 py-2 text-gray-600 dark:text-gray-400 max-w-[150px] truncate" title={batch.batchType === 'TransferBond' ? (batch.defendantName || '') : (batch.associatedReportNumber || '')}>
                            {batch.batchType === 'TransferBond'
                              ? (batch.defendantName || '-')
                              : (batch.associatedReportNumber || '-')}
                          </td>
                          {/* NET Column */}
                          <td className="px-2 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                            {isSelected ? (
                              <div className="flex flex-col items-end gap-0.5">
                                <span className="text-[10px] text-gray-400">{formatCurrencyWithSymbol(netDue)}</span>
                                <input
                                  type="number"
                                  value={netVal ?? formatAmountOnBlur(netDue)}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === '') {
                                      handleAmountChange(batchId, 'net', '');
                                    } else {
                                      const num = parseFloat(val);
                                      handleAmountChange(batchId, 'net', isNaN(num) ? '' : clampCurrency(num, netDue));
                                    }
                                  }}
                                  onBlur={(e) => handleAmountChange(batchId, 'net', formatAmountOnBlur(e.target.value))}
                                  disabled={netDue <= 0}
                                  className="w-20 px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 dark:text-white focus:ring-1 focus:ring-primary text-right disabled:opacity-50 disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  min="0"
                                  max={netDue}
                                  step="0.01"
                                />
                              </div>
                            ) : (
                              <span className="text-gray-600 dark:text-gray-400">
                                {formatCurrencyWithSymbol(netDue)}
                              </span>
                            )}
                          </td>
                          {/* MISC Column */}
                          <td className="px-2 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                            {isSelected ? (
                              <div className="flex flex-col items-end gap-0.5">
                                <span className="text-[10px] text-gray-400">{formatCurrencyWithSymbol(miscDue)}</span>
                                <input
                                  type="number"
                                  value={miscVal ?? formatAmountOnBlur(miscDue)}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === '') {
                                      handleAmountChange(batchId, 'misc', '');
                                    } else {
                                      const num = parseFloat(val);
                                      handleAmountChange(batchId, 'misc', isNaN(num) ? '' : clampCurrency(num, miscDue));
                                    }
                                  }}
                                  onBlur={(e) => handleAmountChange(batchId, 'misc', formatAmountOnBlur(e.target.value))}
                                  disabled={miscDue <= 0}
                                  className="w-20 px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 dark:text-white focus:ring-1 focus:ring-primary text-right disabled:opacity-50 disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  min="0"
                                  max={miscDue}
                                  step="0.01"
                                />
                              </div>
                            ) : (
                              <span className="text-gray-600 dark:text-gray-400">
                                {formatCurrencyWithSymbol(miscDue)}
                              </span>
                            )}
                          </td>
                          {/* BUF Column */}
                          <td className="px-2 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                            {isSelected ? (
                              <div className="flex flex-col items-end gap-0.5">
                                <span className="text-[10px] text-gray-400">{formatCurrencyWithSymbol(bufDue)}</span>
                                <input
                                  type="number"
                                  value={bufVal ?? formatAmountOnBlur(bufDue)}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === '') {
                                      handleAmountChange(batchId, 'buf', '');
                                    } else {
                                      const num = parseFloat(val);
                                      handleAmountChange(batchId, 'buf', isNaN(num) ? '' : clampCurrency(num, bufDue));
                                    }
                                  }}
                                  onBlur={(e) => handleAmountChange(batchId, 'buf', formatAmountOnBlur(e.target.value))}
                                  disabled={bufDue <= 0}
                                  className="w-20 px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 dark:text-white focus:ring-1 focus:ring-primary text-right disabled:opacity-50 disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  min="0"
                                  max={bufDue}
                                  step="0.01"
                                />
                              </div>
                            ) : (
                              <span className="text-gray-600 dark:text-gray-400">
                                {formatCurrencyWithSymbol(bufDue)}
                              </span>
                            )}
                          </td>
                          {/* Total Column */}
                          <td className="px-2 py-2 font-bold text-right whitespace-nowrap">
                            {isSelected ? (
                              <div className="flex flex-col items-end gap-0.5">
                                <span className="text-[10px] text-gray-400">{formatCurrencyWithSymbol(totalDue)}</span>
                                <span className="text-primary dark:text-primary-light">
                                  {formatCurrencyWithSymbol(payingTotal)}
                                </span>
                              </div>
                            ) : (
                              <span className="text-gray-900 dark:text-white">
                                {formatCurrencyWithSymbol(totalDue)}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </div>

              {/* Desktop hero — beside the data on lg+. Hidden on mobile (rendered above the filters there). */}
              <div className="hidden lg:block lg:w-80 lg:flex-shrink-0">{heroPanel}</div>
              </div>
            </>
          )}
        </div>
      </div>
    </OathModal>
  );
};

export default BatchSelectionModal;
